/**
 * 开放平台分块直传（文件库 / 项目 / 收件箱）
 *
 * 基于直传会话协议（/api/open/upload-sessions）：
 *   init → credentials（预签名 PUT 直传对象存储）→ ack → complete → abort
 * 文件字节不经过应用服务器，应用服务器只负责签发凭证、记录进度与合并落地。
 * 支持并发上传、断点续传（同 fileHash 会话自动命中）、进度回调与取消。
 */

/** 计算文件 SHA-256（十六进制小写），用于直传会话的秒传/断点续传
 *  需要 Web Crypto 环境（浏览器 HTTPS/localhost，或 Node.js 15+）
 *  @param {Blob|File} blob
 *  @returns {Promise<string>}
 */
export async function sha256Hex(blob) {
  if (typeof crypto === 'undefined' || !crypto.subtle) {
    throw new Error('当前环境不支持 Web Crypto，请通过 options.fileHash 显式传入文件 SHA-256');
  }
  const digest = await crypto.subtle.digest('SHA-256', await blob.arrayBuffer());
  return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
}

export class DirectUploader {
  /**
   * @param {Object} params
   * @param {Object} params.session - createUploadSession 返回的会话信息
   * @param {HttpClient} params.http
   * @param {IceShuttle} params.client
   * @param {string} [params.collectToken] - collect 会话的收件令牌
   */
  constructor({ session, http, client, collectToken }) {
    this.sessionKey = session.sessionKey;
    this.bizType = session.bizType;
    this.fileName = session.fileName;
    this.fileSize = Number(session.fileSize);
    this.chunkSize = Number(session.chunkSize);
    this.totalChunks = Number(session.totalChunks);
    this.isAccel = !!session.isAccel;
    this.resumed = !!session.resumed;
    this.uploadedChunks = new Set(session.uploadedChunks || []);
    this._http = http;
    this._client = client;
    this._collectToken = collectToken;
    this._canceled = false;
    this._credentialCache = new Map();
    this._partial = new Map(); // chunkIndex -> 已发送字节（并发进度估算用）
  }

  /** 获取会话信息 */
  getInfo() {
    return {
      sessionKey: this.sessionKey,
      bizType: this.bizType,
      fileName: this.fileName,
      fileSize: this.fileSize,
      chunkSize: this.chunkSize,
      totalChunks: this.totalChunks,
      isAccel: this.isAccel,
      resumed: this.resumed
    };
  }

  /** 第 i 块的实际字节数（末块可能不足 chunkSize） */
  _sizeOf(chunkIndex) {
    return chunkIndex === this.totalChunks - 1
      ? this.fileSize - this.chunkSize * (this.totalChunks - 1)
      : this.chunkSize;
  }

  /**
   * 上传文件（分块并发直传对象存储）
   * @param {Blob|File} blob - 文件内容
   * @param {Object} [options]
   * @param {number} [options.concurrency=3] - 并发分块数（1-8）
   * @param {Function} [options.onProgress] - (percent, { uploadedBytes, totalBytes })
   * @returns {Promise<DirectUploader>} 上传完成后返回自身（继续调用 complete 或使用 client.completeUploadSession）
   */
  async upload(blob, options = {}) {
    if (this._canceled) throw new Error('上传已取消');
    const concurrency = Math.max(1, Math.min(Number(options.concurrency) || 3, 8));
    const onProgress = options.onProgress;
    const totalBytes = this.fileSize;

    // 已完成字节数（续传时跳过已 ack 的分块）
    const doneBytes = () => {
      let bytes = 0;
      for (const i of this.uploadedChunks) bytes += this._sizeOf(i);
      return bytes;
    };

    // 待传分块队列
    const pending = [];
    for (let i = 0; i < this.totalChunks; i++) {
      if (!this.uploadedChunks.has(i)) pending.push(i);
    }

    const report = () => {
      if (!onProgress) return;
      let partial = 0;
      for (const v of this._partial.values()) partial += v;
      const bytes = Math.min(doneBytes() + partial, totalBytes);
      onProgress(totalBytes > 0 ? Math.round((bytes / totalBytes) * 100) : 100, {
        uploadedBytes: bytes,
        totalBytes
      });
    };

    let cursor = 0;
    const worker = async () => {
      while (!this._canceled) {
        const idx = cursor++;
        if (idx >= pending.length) return;
        const chunkIndex = pending[idx];
        try {
          await this._uploadChunk(blob, chunkIndex);
        } finally {
          this._partial.delete(chunkIndex);
        }
        report();
      }
    };

    await Promise.all(Array.from({ length: Math.min(concurrency, pending.length) }, worker));

    if (this._canceled) throw new Error('上传已取消');
    return this;
  }

  /** 上传单个分块：取凭证 → PUT 对象存储 → 回执 */
  async _uploadChunk(blob, chunkIndex) {
    const cred = await this._ensureCredential(chunkIndex);
    const offset = chunkIndex * this.chunkSize;
    const chunk = blob.slice(offset, Math.min(offset + this.chunkSize, this.fileSize));

    this._partial.set(chunkIndex, 0);
    await this._putToOss(chunk, cred, (loaded) => {
      this._partial.set(chunkIndex, loaded);
    });

    this.uploadedChunks.add(chunkIndex);
    // 回执容忍单次失败：complete 时服务端会按对象存储实际分块重新校验
    try {
      await this._client.ackUploadChunk(this.sessionKey, chunkIndex, this._collectToken);
    } catch (e) {
      /* ignore ack failure */
    }
  }

  /** 确保持有该分块凭证；缓存未命中时按 64 个一批对齐签发 */
  async _ensureCredential(chunkIndex) {
    if (this._credentialCache.has(chunkIndex)) return this._credentialCache.get(chunkIndex);
    const BATCH = 64;
    const start = Math.floor(chunkIndex / BATCH) * BATCH;
    const batch = [];
    for (let i = start; i < Math.min(start + BATCH, this.totalChunks); i++) {
      if (!this.uploadedChunks.has(i)) batch.push(i);
    }
    const data = await this._client.getUploadCredentials(this.sessionKey, batch, this._collectToken);
    for (const c of (data.credentials || [])) {
      this._credentialCache.set(c.chunkIndex, c);
    }
    const cred = this._credentialCache.get(chunkIndex);
    if (!cred) throw new Error(`分块 ${chunkIndex} 凭证签发失败`);
    return cred;
  }

  /** PUT 分块到对象存储：浏览器优先 XHR（可感知发送进度），其余环境用 fetch */
  _putToOss(chunkBlob, cred, onLoaded) {
    const total = chunkBlob.size;
    if (typeof XMLHttpRequest !== 'undefined') {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable && onLoaded) onLoaded(e.loaded);
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve();
          else reject(new Error(`分块 ${cred.chunkIndex} 上传失败: HTTP ${xhr.status}`));
        };
        xhr.onerror = () => reject(new Error(`分块 ${cred.chunkIndex} 上传网络错误`));
        xhr.open('PUT', cred.uploadUrl, true);
        xhr.send(chunkBlob);
      });
    }
    return chunkBlob.arrayBuffer().then((buf) =>
      fetch(cred.uploadUrl, { method: 'PUT', body: buf })
    ).then((res) => {
      if (!res.ok) throw new Error(`分块 ${cred.chunkIndex} 上传失败: HTTP ${res.status}`);
      if (onLoaded) onLoaded(total);
    });
  }

  /**
   * 取消上传并请求服务端清理已传分块
   */
  async abort() {
    this._canceled = true;
    try {
      await this._client.abortUploadSession(this.sessionKey, this._collectToken);
    } catch (e) {
      /* ignore */
    }
  }
}
